import React from 'react';
import { AbsoluteFill, Sequence, useCurrentFrame } from 'remotion';
import type { CutWindow } from '@/lib/transitions/buildSceneLayout';
import { resolveTransitionStyles } from '@/lib/transitions/resolveTransitionStyles';

/** One stable box for the whole time a scene can be styled. Swapping between
 *  a fragment and a div when the window opens remounts the HTML5 video, which
 *  throws away the premounted buffer and trips pauseWhenBuffering. */
const TransitionStyleShell: React.FC<{
  active: boolean;
  style: React.CSSProperties | null;
  children: React.ReactNode;
}> = ({ active, style, children }) => {
  if (!active) return <>{children}</>;
  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', ...(style ?? {}) }}>
      {children}
    </div>
  );
};

export const SceneTransitionWrapper: React.FC<{
  cuts: CutWindow[];
  sceneIndex: number;
  blockFrom: number;
  children: React.ReactNode;
}> = ({ cuts, sceneIndex, blockFrom, children }) => {
  const local = useCurrentFrame();
  const frame = blockFrom + local;

  let style: React.CSSProperties | null = null;
  let active = false;
  for (const cut of cuts) {
    if (cut.index !== sceneIndex && cut.index !== sceneIndex - 1) continue;
    active = true;
    const s = resolveTransitionStyles(cut, frame);
    if (!s) continue;
    if (cut.index === sceneIndex && s.outgoing) style = { ...(style ?? {}), ...s.outgoing };
    if (cut.index === sceneIndex - 1 && s.incoming) style = { ...(style ?? {}), ...s.incoming };
  }

  // overflow hidden so zoom/whip transforms never bleed outside the frame.
  return (
    <TransitionStyleShell active={active} style={style}>
      {children}
    </TransitionStyleShell>
  );
};

/** Full-frame transform wrapper for keyframe-boundary transitions (the
 *  CropCenterTrack row). Unlike SceneTransitionWrapper this is NOT index-based:
 *  the footage is a single continuous clip across the boundary, so both the
 *  outgoing (pre-boundary) and incoming (post-boundary) halves style the SAME
 *  child. The shell stays mounted for as long as any cut exists, so entering
 *  or leaving a window only updates CSS. An empty `cuts` array stays
 *  unwrapped. */
export const KeyframeTransitionStyles: React.FC<{
  cuts: CutWindow[];
  children: React.ReactNode;
}> = ({ cuts, children }) => {
  const frame = useCurrentFrame();

  let style: React.CSSProperties | null = null;
  for (const cut of cuts) {
    const s = resolveTransitionStyles(cut, frame);
    if (!s) continue;
    if (s.outgoing) style = { ...(style ?? {}), ...s.outgoing };
    if (s.incoming) style = { ...(style ?? {}), ...s.incoming };
  }

  return (
    <TransitionStyleShell active={cuts.length > 0} style={style}>
      {children}
    </TransitionStyleShell>
  );
};

const DipFill: React.FC<{ cut: CutWindow }> = ({ cut }) => {
  const local = useCurrentFrame();
  const s = resolveTransitionStyles(cut, cut.startFrame + local);
  if (!s?.overlay) return null;
  return (
    <AbsoluteFill
      style={{ backgroundColor: s.overlay.color, opacity: s.overlay.opacity, pointerEvents: 'none' }}
    />
  );
};

export const TransitionDipOverlays: React.FC<{ cuts: CutWindow[] }> = ({ cuts }) => (
  <>
    {cuts
      .filter((c) => c.transition.kind === 'dip-to-black' || c.transition.kind === 'dip-to-white')
      .map((cut) => (
        <Sequence
          key={`dip-${cut.index}`}
          from={cut.startFrame}
          durationInFrames={cut.endFrame - cut.startFrame}
        >
          <DipFill cut={cut} />
        </Sequence>
      ))}
  </>
);
